import CommunityScoreWheel, {
  type CommunityScorePercentageType,
} from "@/components/community-score-wheel";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { GameKindEnum } from "@/pages/search/types";

import AwardsPopover, { type AwardType } from "./awards-popover";
import { AwardsIcon, DoorIcon, VirtualIcon } from "./icons";

export const IconsEnum = {
  AWARDS: "AWARDS",
  COUPONS: "COUPONS",
  KIND: "KIND",
  SCORE: "SCORE",
} as const;
export type IconsEnum = (typeof IconsEnum)[keyof typeof IconsEnum];

export const DEFAULT_ICONS = [
  IconsEnum.KIND,
  IconsEnum.COUPONS,
  IconsEnum.AWARDS,
  IconsEnum.SCORE,
];

interface IconStackGameType {
  awards?: AwardType[];
  communityRatingCount?: number;
  communityScoreBucket?: string;
  communityScorePercentages?: CommunityScorePercentageType[];
  kind: GameKindEnum;
}

interface IconStackProps {
  game: IconStackGameType;
  iconClassName?: string;
  icons?: IconsEnum[];
}

const KindTextMap = {
  [GameKindEnum.RER]: "IRL Escape Room",
  [GameKindEnum.VER]: "Virtual Experience",
  [GameKindEnum.TER]: "Tabletop Experience",
};

export default function IconStack({
  game,
  iconClassName,
  icons = DEFAULT_ICONS,
}: IconStackProps) {
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy
  const content = icons.reduce((accum, icon) => {
    if (icon === IconsEnum.AWARDS) {
      if (!(game.awards || []).length) {
        return accum;
      }

      accum.push(
        <AwardsPopover awards={game.awards || []} key={icon}>
          <AwardsIcon className={iconClassName} />
        </AwardsPopover>
      );

      return accum;
    }

    if (icon === IconsEnum.KIND) {
      if (
        ![GameKindEnum.RER, GameKindEnum.VER].includes(
          game.kind as typeof GameKindEnum.RER | typeof GameKindEnum.VER
        )
      ) {
        return accum;
      }

      accum.push(
        <Tooltip key={icon}>
          <TooltipTrigger>
            {game.kind === GameKindEnum.RER && (
              <DoorIcon className={iconClassName} />
            )}
            {game.kind === GameKindEnum.VER && (
              <VirtualIcon className={iconClassName} />
            )}
          </TooltipTrigger>
          <TooltipContent>
            <p>{KindTextMap[game.kind]}</p>
          </TooltipContent>
        </Tooltip>
      );

      return accum;
    }

    if (icon === IconsEnum.SCORE) {
      if (!(game.communityRatingCount && game.communityScorePercentages)) {
        return accum;
      }

      const scoreTooltip = (
        <p key={icon}>
          {game.communityScoreBucket}{" "}
          <span className="text-slate-blue">({game.communityRatingCount})</span>
        </p>
      );

      accum.push(
        <Tooltip key={icon}>
          <TooltipTrigger className="relative flex flex-col gap-[5px]">
            <CommunityScoreWheel
              className={cn("ml-auto size-[17px]", iconClassName)}
              scores={game.communityScorePercentages}
            />
            {game.communityRatingCount >= 3 && (
              <span className="block font-medium text-12/[1em] text-white/80">
                {game.communityRatingCount}
              </span>
            )}
          </TooltipTrigger>
          <TooltipContent>
            <p>{scoreTooltip}</p>
          </TooltipContent>
        </Tooltip>
      );

      return accum;
    }

    return accum;
  }, [] as React.ReactNode[]);

  return <>{content}</>;
}
